import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { SESSION_ID_HEADER } from '@/constant/app'

import { baseService } from './base.service'
import {
  ChangePasswordDto,
  EdunodesJoinCommunityResponse,
  ForgotPasswordDto,
  ResetPasswordDto,
  SignUpResponse,
  UserInformationRegisterDto,
  UserInformationSignInDto,
  VerifyEmailDto,
} from './dto/auth-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { UserProfile } from '@/types/user.type'

export class AuthService {
  private static instance: AuthService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService(baseService)
    }
    return AuthService.instance
  }

  register = async (userInformationRegisterDto: UserInformationRegisterDto) => {
    const { data } = await this.api.post<SignUpResponse>('/signup', userInformationRegisterDto)
    return data
  }

  resetPassword = async (resetPasswordDto: ResetPasswordDto) => {
    const { data } = await this.api.put('/reset-password', resetPasswordDto)
    return data
  }

  verifyAccount = async (verifyEmailDto: VerifyEmailDto) => {
    const { data } = await this.api.post('/verify-account', verifyEmailDto)
    return data
  }

  resendEmailVerification = async (email: string, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.post('/resend-email-verification', { email }, configs)
    return data
  }

  changePassword = async (changePasswordDto: ChangePasswordDto, userId: number, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.put(`/change-password/${userId}`, changePasswordDto, configs)
    return data
  }

  forgotPassword = async (forgotPasswordDto: ForgotPasswordDto) => {
    const { data } = await this.api.post('/forgot-password', forgotPasswordDto)
    return data
  }

  requestAccessToken = async (refreshToken: string) => {
    const { data } = await this.api.post('/request-access-token', {
      refreshToken,
    })
    return data
  }

  /**
   * On non-fsds origins the backend includes JWT tokens in the response body
   * (cookies can't reach the browser there). Strip them out so callers get a
   * clean user payload as before, and surface them alongside sessionId.
   */
  verifyEdunodesPlatform = async (userSignInDto: UserInformationSignInDto) => {
    const { data } = await this.api.post<EdunodesJoinCommunityResponse>(
      '/edunodes-verify-platform',
      userSignInDto
    )
    return data
  }

  joinEdunodesCommunity = async <T>(
    userSignInDto: UserInformationSignInDto
  ): Promise<{
    data: T
    sessionId?: string
    accessToken?: string
    refreshToken?: string
    accessTokenExpiresIn?: string
    refreshTokenExpiresIn?: string
  }> => {
    const response = await this.api.post<
      T & {
        accessToken?: string
        refreshToken?: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      }
    >('/edunodes-join-community', userSignInDto)
    const sessionId = response.headers[SESSION_ID_HEADER] as string

    const body = response.data || ({} as Record<string, unknown>)
    const { accessToken, refreshToken, accessTokenExpiresIn, refreshTokenExpiresIn, ...userData } =
      body as Record<string, unknown> & {
        accessToken?: string
        refreshToken?: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      }
    return {
      data: userData as T,
      sessionId,
      accessToken,
      refreshToken,
      accessTokenExpiresIn,
      refreshTokenExpiresIn,
    }
  }

  login = async <T>(
    userSignInDto: UserInformationSignInDto
  ): Promise<{
    data: T
    sessionId?: string
    accessToken?: string
    refreshToken?: string
    accessTokenExpiresIn?: string
    refreshTokenExpiresIn?: string
  }> => {
    const response = await this.api.post<
      T & {
        accessToken?: string
        refreshToken?: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      }
    >('/login', userSignInDto)
    const sessionId = response.headers[SESSION_ID_HEADER] as string

    const body = response.data || ({} as Record<string, unknown>)
    const { accessToken, refreshToken, accessTokenExpiresIn, refreshTokenExpiresIn, ...userData } =
      body as Record<string, unknown> & {
        accessToken?: string
        refreshToken?: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      }
    return {
      data: userData as T,
      sessionId,
      accessToken,
      refreshToken,
      accessTokenExpiresIn,
      refreshTokenExpiresIn,
    }
  }

  /**
   * Exchange an eztech-minted platform JWT for a tenant session (current account).
   */
  createPlatformSession = async <T>(
    platformToken: string
  ): Promise<{
    data: T
    sessionId?: string
    accessToken?: string
    refreshToken?: string
    accessTokenExpiresIn?: string
    refreshTokenExpiresIn?: string
  }> => {
    const response = await this.api.post<
      {
        user?: T
        accessToken?: string
        refreshToken?: string
        accessTokenExpiresIn?: string
        refreshTokenExpiresIn?: string
      } & T
    >('/platform-session', { platformToken })
    const sessionId = response.headers[SESSION_ID_HEADER] as string

    const body = response.data || ({} as Record<string, unknown>)
    const {
      accessToken,
      refreshToken,
      accessTokenExpiresIn,
      refreshTokenExpiresIn,
      user,
      ...rest
    } = body as Record<string, unknown> & {
      accessToken?: string
      refreshToken?: string
      accessTokenExpiresIn?: string
      refreshTokenExpiresIn?: string
      user?: T
    }
    const userData = (user ?? rest) as T
    return {
      data: userData,
      sessionId,
      accessToken,
      refreshToken,
      accessTokenExpiresIn,
      refreshTokenExpiresIn,
    }
  }

  refreshAccessToken = async (
    refreshToken: string
  ): Promise<{
    accessToken: string
    refreshToken: string
    accessTokenExpiresIn: string
    refreshTokenExpiresIn: string
    user: UserProfile
  }> => {
    const { data } = await this.api.post('/refresh-token', { refreshToken })
    return data
  }

  logout = async (configs?: AxiosRequestConfig) => {
    const { data } = await this.api.post('/logout', configs)
    return data
  }

  getCurrentUser = async (configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<UserProfile>('/current-user', configs)
    return data
  }
}

const authService = AuthService.getInstance(baseService)

export default authService
